import {
  TableContainer,
  Table,
  Tr,
  Tbody,
  Td,
  Flex,
  Heading,
  Text,
  TableContainerProps,
  Box,
  Input,
  Tooltip,
} from '@chakra-ui/react';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';

import { BaseSample, Sample } from '@texas/api/endpoints/samplesApi';
import { PaImage } from '../productApproval/PaImage';
import { AngularNavLink } from '../AngularNavLink';
import { PortalRefProvider, usePortalRef } from '@texas/hooks/usePortalRef';
import { PaIcon } from '../productApproval/PaIcon';
import { SampleShipment } from '@texas/api/endpoints/shipmentsApi';
import { VerifyIconButton } from '../verifyButton/VerifyIconButton';
import { defaultIconSize, Icons } from '../Icons';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { Locale } from '@texas/i18n/types';

interface Props extends TableContainerProps {
  samples: BaseSample[] | SampleShipment[] | Sample[];
  onRemoveFromTransportRequest?: (id: number) => void;
  onRemoveLoading?: boolean;
  onChangeSupplierItemCode?: (
    sampleId: number,
    supplierItemCode: string,
  ) => void;
}

export function SamplesTable({
  samples,
  onRemoveFromTransportRequest,
  onRemoveLoading,
  onChangeSupplierItemCode,
  ...props
}: Props) {
  const { t } = useTranslation();
  const portalRef = usePortalRef();
  return (
    <PortalRefProvider value={portalRef}>
      {samples.length === 0 && <Text>{t('sample.noSamplesFound')}</Text>}
      <TableContainer w="full" {...props}>
        <Table variant="samples" {...portalRef.bindRef}>
          <Tbody>
            {samples.map((x) => (
              <Tr
                key={x.sampleId}
                animation={fadeInScaleAnimation()}
                pos="relative"
                sx={{
                  ':hover *[data-hover-id="remove"]': {
                    opacity: 1,
                    transform: 'scale(1)',
                  },
                }}
              >
                <Td>
                  <PaImage
                    fileIdentifier={x.productApproval.fileIdentifier}
                    fileId={x.productApproval.fileId}
                    paId={x.productApproval.productApprovalId}
                    paNumber={x.productApproval.number}
                    itemCode={x.articleItemCode}
                    articleFolderId={x.articleFolderId}
                    matrixNodeXValue={x.matrixNodeXValue}
                    matrixNodeYValue={x.matrixNodeYValue}
                    maxH="88px"
                    maxW="100%"
                  />
                </Td>
                <Td verticalAlign="baseline">
                  <Flex flexDir="column">
                    <Heading fontSize="md">
                      <AngularNavLink
                        to="variant.versions"
                        navobj={{
                          articleId: x.articleId,
                          variantId: x.variantId,
                        }}
                      >
                        {x.articleItemCode}
                      </AngularNavLink>
                    </Heading>
                    <Text fontSize="sm" variant="sub">
                      {x.articleName}
                    </Text>
                    <Text fontSize="sm">{x.matrixNodeXValue}</Text>
                    <Text fontSize="sm">{x.matrixNodeYValue}</Text>
                  </Flex>
                </Td>
                <Td verticalAlign="baseline">
                  <Heading whiteSpace="break-spaces" fontSize="md">
                    {x.supplierName}
                  </Heading>
                  <Text fontSize="sm" variant="sub">
                    {x.branchName}
                  </Text>
                  {x.sampleReadyDate && (
                    <Tooltip label={t('sample.expectedReadyDate')}>
                      <Flex w="fit-content" gap={2} align="center">
                        <Icons.CalendarCheck
                          boxSize={defaultIconSize}
                          color="gray.200"
                          _light={{ color: 'gray.400' }}
                        />
                        <Text fontSize="sm" variant="sub">
                          {formatDate(Locale.En, x.sampleReadyDate)}
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}

                  {onChangeSupplierItemCode && (
                    <Flex direction="column">
                      <Input
                        name="supplierItemCode"
                        defaultValue={
                          x.productApproval.externalSupplierItemCode
                        }
                        bg="transparent !important"
                        px={1}
                        pb={3}
                        maxLength={20}
                        variant="texas"
                        onChange={(e) =>
                          onChangeSupplierItemCode(x.sampleId, e.target.value)
                        }
                      />
                      <Text fontSize="xs" variant="sub">
                        {t('productApproval.externalItemCode')}
                      </Text>
                    </Flex>
                  )}
                </Td>
                <Td verticalAlign="baseline">
                  <Flex flexDir="column">
                    <Flex gap={2} align="center">
                      <Heading fontSize="md">
                        <AngularNavLink
                          to="article.productApproval"
                          navobj={{
                            articleId: x.articleId,
                            searchTerm: x.productApproval.number,
                            state: x.productApproval.state,
                          }}
                        >
                          {x.productApproval.formatNumber}
                        </AngularNavLink>
                      </Heading>
                      <PaIcon state={x.productApproval.state} opacity={0.6} />
                    </Flex>
                    {x.customerName && (
                      <Text variant="small">
                        <AngularNavLink
                          whiteSpace="break-spaces"
                          to="customer"
                          navobj={{
                            customerId: x.customerId,
                          }}
                        >
                          {x.customerName}
                        </AngularNavLink>
                      </Text>
                    )}
                    <Text variant="small">
                      {x.productApproval.contact.name}
                    </Text>
                  </Flex>
                </Td>
                {'transport' in x && x.transport?.receiverUser !== null && (
                  <Td verticalAlign="baseline">
                    <Text fontSize="sm" variant="sub">
                      {x.transport?.receiverUser.name}
                    </Text>
                  </Td>
                )}
                {onRemoveFromTransportRequest && (
                  <Td>
                    <Box
                      transition="transform 200ms ease, opacity 200ms ease"
                      opacity={0}
                      transform="scale(0.9)"
                      data-hover-id="remove"
                    >
                      <VerifyIconButton
                        buttonProps={{ size: 'sm', isLoading: onRemoveLoading }}
                        tooltipLabel={t('general.clickToRemove')}
                        icon={<Icons.Close />}
                        onVerified={() =>
                          onRemoveFromTransportRequest(x.sampleId)
                        }
                      />
                    </Box>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </PortalRefProvider>
  );
}
